import type {
  ComponentProps,
  WithGatsbyImage,
  WithReference,
} from "@ts/components";
import type {
  Article,
  ArticleCategory,
  CustomImage,
  Sections,
} from "@ts/sanity";
import React, { type FC } from "react";

export type ArticleInputProps = ComponentProps & {
  article: WithReference<Article, "categories", ArticleCategory[]>;
  relatedArticles:
    | {
        edges: {
          next: Article;
          node: Article;
          previous: Article;
        }[];
      }
    | {
        node: Article;
        previous: Article;
        next: Article;
      };
};

export type ArticleOutputProps = WithGatsbyImage<
  {
    content: {
      content: Sections;
    };
    date: string;
    next: Article;
    paths: {
      title: string;
      url?: string;
    }[];
    previous: Article;
    shareImage: CustomImage;
    summary: string;
    title: string;
  },
  "shareImage"
>;

export const withArticle =
  (Component: FC<ArticleOutputProps>) =>
  ({ name = "Article", article, relatedArticles }: ArticleInputProps) => {
    const { title, content, _createdAt, attributes, slug, image, summary } =
      article;
    const { next, previous } =
      relatedArticles?.edges?.find(
        (featArticle) => featArticle.node?.slug?.current === slug?.current,
      ) ||
      relatedArticles ||
      {};
    const date = attributes?.publishedAt
      ? new Date(attributes?.publishedAt).toLocaleDateString()
      : new Date(_createdAt).toLocaleDateString();
    const page = {
      content: content,
    };

    const paths = [
      {
        title: "Journal",
        url: "/articles",
      },
    ];

    Component.displayName = name;
    return (
      <Component
        title={title}
        content={page}
        date={date}
        paths={paths}
        next={next}
        previous={previous}
        shareImage={image}
        summary={summary}
      />
    );
  };
